import { Injectable } from '@angular/core';
import { PageTitleService } from './page-title.service';
import { AnalyticsService } from './analytics.service';
import { AnalyticsEvents } from './analytics-config';

/**
 * Service for tracking hierarchical navigation
 * Combines page title updates with detailed event tracking
 */
@Injectable({
  providedIn: 'root'
})
export class NavigationTrackerService {
  private currentContext: NavigationContext = {
    section: '',
    viewMode: '',
    feature: '',
    datasetId: '',
    datasetName: '',
    accountName: ''
  };

  constructor(
    private pageTitleService: PageTitleService,
    private analytics: AnalyticsService
  ) {}

  /**
   * Track navigation to a feature within a view
   * Updates both page title and sends analytics event
   */
  trackFeatureNavigation(params: FeatureNavigationParams, datasetId: string): void {
    // Update page title for user visibility
    this.pageTitleService.setHierarchicalTitle(
      params.baseView,
      params.feature,
      params.datasetName
    );

    // Update current context
    this.currentContext = {
      section: params.section,
      viewMode: params.viewMode,
      feature: params.feature
    };

    // Track detailed analytics event
    this.analytics.trackEvent(datasetId + '_' + AnalyticsEvents.FEATURE_ACCESSED, {
      page_section: params.section,
      view_mode: params.viewMode,
      feature_name: this.sanitizeFeatureName(params.feature),
      parent_view: `${params.section}_${params.viewMode}`,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track navigation back to base view
   * Resets title and tracks exit from feature
   */
  trackBaseViewNavigation(params: BaseViewParams): void {
    const previousFeature = this.currentContext.feature;

    // Update page title
    const viewTitle = params.viewMode === 'map' 
      ? `Datapoints (Map) - ${params.datasetName}`
      : params.viewMode === 'table'
      ? `Datapoints (Table) - ${params.datasetName}`
      : `Dashboard - ${params.datasetName}`;
    
    this.pageTitleService.setTitle(viewTitle);

    // Track feature exit if coming from a feature
    if (previousFeature) {
      this.analytics.trackEvent(params.accountName + AnalyticsEvents.FEATURE_EXITED, {
        page_section: params.section,
        view_mode: params.viewMode,
        feature_name: this.sanitizeFeatureName(previousFeature),
        dataset_id: params.datasetId,
        dataset_name: params.datasetName
      });
    }

    // Reset context
    this.currentContext = {
      section: params.section,
      viewMode: params.viewMode,
      feature: '',
      datasetId: params.datasetId || '',
      datasetName: params.datasetName || '',
      accountName: params.accountName || ''
    };
  }

  /**
   * Track multi-level navigation (e.g., Panel > Sub-feature)
   */
  trackDeepNavigation(params: DeepNavigationParams): void {
    // Build hierarchical title
    const hierarchyPath = `${params.baseView} > ${params.parentFeature} > ${params.subFeature}`;
    this.pageTitleService.setTitle(`${hierarchyPath} - ${params.datasetName} - Maptycs`);

    // Track with full hierarchy
    this.analytics.trackEvent(AnalyticsEvents.FEATURE_ACCESSED, {
      page_section: params.section,
      view_mode: params.viewMode,
      parent_feature: this.sanitizeFeatureName(params.parentFeature),
      feature_name: this.sanitizeFeatureName(params.subFeature),
      dataset_id: params.datasetId,
      dataset_name: params.datasetName,
      hierarchy_level: 3,
      hierarchy_path: `${params.section}/${params.viewMode}/${params.parentFeature}/${params.subFeature}`
    });
  }

  /**
   * Get current navigation context
   */
  getCurrentContext(): NavigationContext {
    return { ...this.currentContext };
  }

  /**
   * Sanitize feature name for analytics
   * Converts "Analytics Panel" to "analytics_panel"
   */
  private sanitizeFeatureName(featureName: string): string {
    return featureName
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');
  }
}

// Type definitions
export interface FeatureNavigationParams {
  section: string;           // 'datapoints', 'dashboard', etc.
  viewMode: string;          // 'map', 'table', 'dashboard'
  baseView: string;          // 'Datapoints (Map)', 'Dashboard', etc.
  feature: string;           // 'Analytics Panel', 'Filter Panel', etc.
  datasetId?: string;
  datasetName?: string;
  accountName?: string;
}

export interface BaseViewParams {
  section: string;
  viewMode: string;
  datasetId?: string;
  datasetName?: string;
  accountName?: string;
}

export interface DeepNavigationParams {
  section: string;
  viewMode: string;
  baseView: string;
  parentFeature: string;     // 'Analytics Panel'
  subFeature: string;        // 'Chart Settings'
  datasetId?: string;
  datasetName?: string;
}

export interface NavigationContext {
  section: string;
  viewMode: string;
  feature: string;
  datasetId?: string;
  datasetName?: string;
  accountName?: string;
}
